import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ChevronRight, LockOutlined, Public, Shield } from "@mui/icons-material";
import { colors } from "../../theme/colors";
import MotionFade from "../common/MotionFade";
import LobbyMapPool from "./LobbyMapPool";
import SystemStatusBar from "./SystemStatusBar.jsx";
import {
    LOBBY_SETUP_DECK_GAP_SX,
    LOBBY_SETUP_DECK_GAP_MOBILE,
    LOBBY_SETUP_DECK_GAP_SHORT,
    LOBBY_SETUP_DECK_MAX_WIDTH,
    LOBBY_OURO_BORDER,
    LOBBY_OURO_SHADOW,
    LOBBY_OURO_SURFACE,
    MOBILE_NAV_MQ,
    SHORT_VIEWPORT_MQ,
} from "./lobbyPageStyles";

function SetupSummary({
    icon: Icon,
    label,
    summary,
    isEmpty,
    fadeKey,
}) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: "0.6rem",
                minWidth: 0,
                flex: "1 1 0",
            }}
        >
            <Box
                aria-hidden
                sx={{
                    position: "relative",
                    width: { xs: "1.8rem", xl: "2rem" },
                    height: { xs: "1.8rem", xl: "2rem" },
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                    borderRadius: "10px",
                    border: `1px solid ${alpha(colors.primary.main, 0.18)}`,
                    background: `
                        linear-gradient(
                            180deg,
                            ${alpha(colors.primary.main, 0.12)},
                            ${alpha("#fff", 0.025)}
                        )
                    `,
                    boxShadow: `
                        inset 0 1px 0 rgba(255,255,255,0.05),
                        0 6px 16px rgba(0,0,0,0.24),
                        0 0 18px ${alpha(colors.primary.main, 0.07)}
                    `,
                    [MOBILE_NAV_MQ]: {
                        ml: "0.5rem",
                    },
                }}
            >
                <Icon
                    sx={{
                        fontSize: { xs: "1rem", xl: "1.12rem" },
                        color: colors.accent.soft,
                        filter: `drop-shadow(0 0 5px ${alpha(colors.primary.main, 0.38)})`,
                    }}
                />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                    sx={{
                        fontSize: { xs: "0.58rem", xl: "0.62rem" },
                        fontWeight: 700,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: alpha(colors.accent.soft, 0.7),
                        lineHeight: 1.2,
                        mb: "0.16rem",
                    }}
                >
                    {label}
                </Typography>
                <MotionFade
                    in
                    durationMs={240}
                    key={fadeKey}
                    style={{ minWidth: 0, overflow: "hidden" }}
                >
                    <Typography
                        sx={{
                            fontSize: { xs: "0.76rem", xl: "0.82rem" },
                            fontWeight: isEmpty ? 600 : 700,
                            color: isEmpty
                                ? alpha(colors.text.muted, 0.88)
                                : alpha(colors.text.primary, 0.92),
                            lineHeight: 1.25,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {summary}
                    </Typography>
                </MotionFade>
            </Box>
        </Box>
    );
}

SetupSummary.propTypes = {
    icon: PropTypes.elementType.isRequired,
    label: PropTypes.string.isRequired,
    summary: PropTypes.string.isRequired,
    isEmpty: PropTypes.bool,
    fadeKey: PropTypes.string.isRequired,
};

const SETUP_PANEL_HOVER_BG = "rgba(255,255,255,0.045)";

const SETUP_PANEL_INTERACTIVE_SX = {
    cursor: "pointer",
    borderRadius: "10px",
    transition: "background-color 0.15s ease-in-out",
    "&:hover": {
        backgroundColor: SETUP_PANEL_HOVER_BG,
    },
    "&:focus-visible": {
        outline: `2px solid ${alpha(colors.accent.soft, 0.72)}`,
        outlineOffset: "2px",
    },
};

function handleSetupPanelKeyDown(event, onActivate) {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onActivate();
}

export function LobbySetupEditCue({ hidden = false }) {
    return (
        <Box
            className="lobbySetupEditCue"
            aria-hidden
            sx={{
                display: hidden ? "none" : "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.15rem",
                flex: "0 0 auto",
                border: 0,
                background: "transparent",
                color: alpha(colors.accent.soft, 0.58),
                px: 0,
                py: 0,
                boxShadow: "none",
                pointerEvents: "none",
                transition: "color 0.15s ease-in-out",
                [MOBILE_NAV_MQ]: {
                    display: "none",
                },
            }}
        >
            <Typography
                component="span"
                sx={{
                    fontSize: "0.62rem",
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                }}
            >
                Edit
            </Typography>
            <ChevronRight sx={{ fontSize: "0.85rem" }} />
        </Box>
    );
}

LobbySetupEditCue.propTypes = {
    hidden: PropTypes.bool,
};

export default function LobbyMatchSetupDeck({
    shouldBlockActions,
    actions,
    lobbyMapsToDisplay,
    lobbyServerLocationsToDisplay,
    serverLocationsRecapSummary,
    lobbyOptionsToDisplay,
    matchmakingRecapSummary,
    canEditLobbySettings,
    currentLobbyCode,
    platformStats,
    onOpenSettings,
}) {
    const isLeaderLocked = Boolean(currentLobbyCode) && !canEditLobbySettings;

    const handleEdit = (sectionId) => {
        if (shouldBlockActions()) {
            actions.showUnavailableActionMessage();
            return;
        }
        onOpenSettings?.(sectionId);
    };

    const getSetupPanelEditProps = (sectionId) => ({
        role: "button",
        tabIndex: 0,
        onClick: () => handleEdit(sectionId),
        onKeyDown: (event) =>
            handleSetupPanelKeyDown(event, () => handleEdit(sectionId)),
    });

    return (
        <Box
            className="lobbyMatchSetupDeck"
            sx={{
                width: "100%",
                maxWidth: LOBBY_SETUP_DECK_MAX_WIDTH,
                display: "grid",
                gridTemplateColumns: "minmax(0, 1.55fr) minmax(18rem, 0.85fr)",
                gap: LOBBY_SETUP_DECK_GAP_SX,
                mx: "auto",
                px: { xs: "0.85rem", xl: "1.15rem" },
                pt: { xs: "1rem", xl: "1.15rem" },
                pb: { xs: "0.85rem", xl: "1rem" },
                boxSizing: "border-box",
                position: "relative",
                isolation: "isolate",
                overflow: "hidden",
                border: `1px solid ${LOBBY_OURO_BORDER}`,
                borderRadius: "12px",
                background: LOBBY_OURO_SURFACE,
                backdropFilter: "blur(18px)",
                WebkitBackdropFilter: "blur(18px)",
                boxShadow: LOBBY_OURO_SHADOW,
                "&::after": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    background: `
                        radial-gradient(
                            circle at 36% 118%,
                            ${alpha(colors.primary.main, 0.07)},
                            transparent 36%
                        )
                    `,
                    pointerEvents: "none",
                    zIndex: -1,
                },
                [MOBILE_NAV_MQ]: {
                    gridTemplateColumns: "minmax(0, 1fr)",
                    gap: LOBBY_SETUP_DECK_GAP_MOBILE,
                },
                [SHORT_VIEWPORT_MQ]: {
                    gap: LOBBY_SETUP_DECK_GAP_SHORT,
                },
            }}
        >
            <Box
                {...getSetupPanelEditProps("map-pool")}
                aria-label="Open map pool settings"
                sx={{
                    ...SETUP_PANEL_INTERACTIVE_SX,
                    minWidth: 0,
                    px: { xs: "0.35rem", xl: "0.45rem" },
                    py: { xs: "0.35rem", xl: "0.45rem" },
                    boxSizing: "border-box",
                    "&:hover .lobbySetupEditCue": {
                        color: colors.accent.soft,
                    },
                }}
            >
                <LobbyMapPool
                    compact
                    selectedMaps={lobbyMapsToDisplay}
                    headerEnd={<LobbySetupEditCue hidden={isLeaderLocked} />}
                />
            </Box>

                <Box
                    className="lobbySetupPrefsPanel"
                    onClick={() => handleEdit("match-filters")}
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "stretch",
                        justifyContent: "center",
                        gap: { xs: "0.65rem", xl: "0.75rem" },
                        minWidth: 0,
                        borderLeft: "1px solid rgba(255,255,255,0.065)",
                        pl: { xs: "1rem", xl: "1.2rem" },
                        pr: { xs: "0.35rem", xl: "0.45rem" },
                        py: "0.4rem",
                        boxSizing: "border-box",
                        ...SETUP_PANEL_INTERACTIVE_SX,
                        background: `
                            linear-gradient(
                                90deg,
                                rgba(255,255,255,0.018),
                                transparent 78%
                            )
                        `,
                        "&:hover": {
                            ...SETUP_PANEL_INTERACTIVE_SX["&:hover"],
                        },
                        "&:hover .lobbySetupEditCue": {
                            color: colors.accent.soft,
                        },
                        [MOBILE_NAV_MQ]: {
                            borderLeft: 0,
                            borderTop: "1px solid rgba(255,255,255,0.065)",
                            pl: 0,
                            pr: 0,
                            pt: "0.75rem",
                            background: "transparent",
                            "&:hover": {
                                backgroundColor: SETUP_PANEL_HOVER_BG,
                            },
                        },
                        [SHORT_VIEWPORT_MQ]: {
                            gap: "0.55rem",
                        },
                    }}
                >
                    <Box
                        {...getSetupPanelEditProps("server-locations")}
                        aria-label="Open server location settings"
                        onClick={(event) => {
                            event.stopPropagation();
                            handleEdit("server-locations");
                        }}
                        onKeyDown={(event) => {
                            event.stopPropagation();
                            handleSetupPanelKeyDown(event, () =>
                                handleEdit("server-locations"),
                            );
                        }}
                        sx={{
                            cursor: "pointer",
                            px: "0.25rem",
                            py: "0.2rem",
                            borderRadius: "10px",
                            "&:focus-visible":
                                SETUP_PANEL_INTERACTIVE_SX["&:focus-visible"],
                        }}
                    >
                        <SetupSummary
                            icon={Public}
                            label="Regions"
                            summary={
                                serverLocationsRecapSummary?.text || "All regions"
                            }
                            isEmpty={serverLocationsRecapSummary?.isEmpty}
                            fadeKey={(lobbyServerLocationsToDisplay || []).join(
                                ",",
                            )}
                        />
                    </Box>
                    <Box
                        aria-hidden
                        sx={{
                            width: "100%",
                            height: "1px",
                            background: `
                                linear-gradient(
                                    90deg,
                                    transparent,
                                    rgba(255,255,255,0.1),
                                    transparent
                                )
                            `,
                        }}
                    />
                    <Box
                        sx={{
                            px: "0.25rem",
                            py: "0.2rem",
                        }}
                    >
                        <SetupSummary
                            icon={Shield}
                            label="Preferences"
                            summary={
                                matchmakingRecapSummary?.text ||
                                "Default preferences"
                            }
                            isEmpty={matchmakingRecapSummary?.isEmpty}
                            fadeKey={JSON.stringify(lobbyOptionsToDisplay || [])}
                        />
                    </Box>
                    <Box sx={{ alignSelf: "flex-end" }}>
                        <LobbySetupEditCue hidden={isLeaderLocked} />
                    </Box>
                </Box>

            {isLeaderLocked && (
                <Box
                    sx={{
                        gridColumn: { xs: "2", xl: "2" },
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "0.35rem",
                        color: alpha(colors.text.secondary, 0.58),
                        [MOBILE_NAV_MQ]: {
                            gridColumn: "1",
                            justifyContent: "center",
                        },
                    }}
                >
                    <LockOutlined sx={{ fontSize: "0.72rem" }} />
                    <Typography
                        sx={{
                            fontSize: "0.62rem",
                            letterSpacing: "0.03em",
                            lineHeight: 1.3,
                            textAlign: "center",
                        }}
                    >
                        Only the party leader can edit lobby settings
                    </Typography>
                </Box>
            )}

            <Box
                sx={{
                    gridColumn: "1 / -1",
                    position: "relative",
                    minWidth: 0,
                    mt: { xs: "0.2rem", xl: "0.35rem" },
                    pt: { xs: "0.65rem", xl: "0.8rem" },
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "1px",
                        background: `
                            linear-gradient(
                                90deg,
                                transparent,
                                rgba(255,255,255,0.09) 18%,
                                ${alpha(colors.primary.main, 0.42)} 50%,
                                rgba(255,255,255,0.09) 82%,
                                transparent
                            )
                        `,
                        boxShadow: `0 0 12px ${alpha(colors.primary.main, 0.08)}`,
                    },
                }}
            >
                <Typography
                    sx={{
                        mb: { xs: "0.22rem", xl: "0.28rem" },
                        textAlign: "center",
                        fontSize: "0.54rem",
                        fontWeight: 700,
                        letterSpacing: "0.15em",
                        textTransform: "uppercase",
                        color: alpha(colors.text.secondary, 0.62),
                        lineHeight: 1,
                    }}
                >
                    System Status
                </Typography>
                <SystemStatusBar platformStats={platformStats} embedded />
            </Box>
        </Box>
    );
}

LobbyMatchSetupDeck.propTypes = {
    shouldBlockActions: PropTypes.func.isRequired,
    actions: PropTypes.object.isRequired,
    lobbyMapsToDisplay: PropTypes.array,
    lobbyServerLocationsToDisplay: PropTypes.array,
    serverLocationsRecapSummary: PropTypes.object,
    lobbyOptionsToDisplay: PropTypes.object,
    matchmakingRecapSummary: PropTypes.object,
    canEditLobbySettings: PropTypes.bool,
    currentLobbyCode: PropTypes.string,
    platformStats: PropTypes.object,
    onOpenSettings: PropTypes.func,
};
